import React from "react";
import { useGetParticipantsListForCalendarInvite, useSendCalendarInvite } from "./dataloader";
import { SendCalendarInviteByActivityAdmin } from "./type";
import { ApiActivityEventParticipantCalendarInvite } from "shared/types/ApiActivityEventParticipantCalendarInvite";
import { useParams } from "react-router-dom";
import sortBy from "lodash/sortBy";
import { useCommonFeatures } from "context/commonFeatures";
import { useCopies } from "shared/hooks/useCopies";
import Variant from "shared/enums/Variant";

export function useSendCalendarInviteByActivityAdmin(): SendCalendarInviteByActivityAdmin {
  const { data: participants } = useGetParticipantsListForCalendarInvite();
  const { mutateAsync: sendCalendarInvite } = useSendCalendarInvite();
  const { programParticipantId = "", agendaId = "" } = useParams();
  const [trackedCalendarInvites, setTrackedCalendarInvites] = React.useState<Array<string>>([]);
  const [searchText, setSearchText] = React.useState("");
  const { initConfirmationAlert } = useCommonFeatures();
  const copies = useCopies();
  const data = React.useMemo(() => {
    return sortBy(participants, ["firstName", "lastName"]);
  }, [participants]);

  const filteredParticipants = React.useMemo(() => {
    if (!searchText) {
      return data;
    }
    return data?.filter(participant => {
      const fullName = `${participant.firstName} ${participant.lastName}`;
      return (
        fullName.toLowerCase().includes(searchText.toLowerCase()) ||
        participant.jobTitle?.toLowerCase().includes(searchText.toLowerCase())
      );
    });
  }, [searchText, data]);

  const handleSendCalendarInvite = React.useCallback(
    async (eventParticipantId: string) => {
      const request: ApiActivityEventParticipantCalendarInvite = {
        activityAgendaId: agendaId,
        activityAdminId: programParticipantId,
        eventParticipantId
      };
      const participant = participants?.find(participant => participant.id === eventParticipantId);
      initConfirmationAlert({
        message: copies.get("associate.program.notify.colleague.confirmation", {
          0: `${participant?.firstName} ${participant?.lastName}`
        }),
        handleConfirm: async () => {
          try {
            await sendCalendarInvite(request);
            setTrackedCalendarInvites(t => [...t, eventParticipantId]);
          } catch (error) {}
        },
        primaryButtonVariant: Variant.SUCCESS
      });
    },
    [agendaId, programParticipantId, participants, initConfirmationAlert, copies, sendCalendarInvite]
  );

  return {
    participants: data,
    handleSendCalendarInvite,
    filteredParticipants,
    searchText,
    setSearchText,
    trackedCalendarInvites
  };
}
